export type LegalSection = {
  heading: string
  paragraphs?: string[]
  bullets?: string[]
}

export type LegalPageData = {
  slug: string
  title: string
  summary: string
  meta: Array<{ label: string; value: string }>
  sections: LegalSection[]
}

const LEGAL_DATE = 'May 29, 2026'

export const LEGAL_FOOTER_LINKS = [
  { label: 'Terms of Service', path: '/terms' },
  { label: 'Privacy Policy', path: '/privacy' },
  { label: 'Security', path: '/security' },
  { label: 'Cookie Policy', path: '/cookies' },
  { label: 'Accessibility', path: '/accessibility' },
  { label: 'Contact Us', path: '/contact' },
] as const

export const LEGAL_PAGES: Record<string, LegalPageData> = {
  terms: {
    slug: 'terms',
    title: 'Terms of Service',
    summary: 'These terms govern access to the PossoBuild platform and all related products, including SmartHire, SmartAssess, SmartDevelop, and SmartAssist.',
    meta: [
      { label: 'Effective Date', value: LEGAL_DATE },
      { label: 'Last Updated', value: LEGAL_DATE },
      { label: 'URL', value: 'possobuild.ai/terms' },
    ],
    sections: [
      {
        heading: '1. Acceptance of Terms',
        paragraphs: [
          'By accessing or using the PossoBuild platform and any of its products — including SmartHire, SmartAssess, SmartDevelop, and SmartAssist — you agree to be bound by these Terms of Service.',
          'If you are using PossoBuild on behalf of an organisation, you represent that you have the authority to bind that organisation to these terms.',
        ],
      },
      {
        heading: '2. Description of Services',
        paragraphs: [
          'PossoBuild provides an AI-powered Workforce Intelligence Platform that enables organisations to conduct structured interviews, assess skills, support employee development, and access enterprise knowledge assistance.',
          'Services are provided on a subscription basis as agreed in your Order Form or Pilot Agreement.',
        ],
      },
      {
        heading: '3. Acceptable Use',
        bullets: [
          'Use the platform for any unlawful purpose or in violation of any applicable regulation.',
          'Attempt to reverse-engineer, copy, or replicate any part of the PossoBuild platform or its AI models.',
          'Upload content that is discriminatory, defamatory, or violates any third-party rights.',
          'Share login credentials or allow unauthorised access to your account.',
          'Use the platform to make automated hiring decisions without human review.',
        ],
      },
      {
        heading: '4. Intellectual Property',
        paragraphs: [
          'All intellectual property rights in the PossoBuild platform, including its AI models, algorithms, interfaces, and documentation, are owned by PossoBuild or its licensors.',
          'Your organisation retains ownership of the data you input into the platform. PossoBuild does not claim ownership over candidate data or interview content.',
        ],
      },
      {
        heading: '5. Limitation of Liability',
        paragraphs: [
          'PossoBuild provides tools to support — not replace — human hiring and talent decisions.',
          'To the maximum extent permitted by law, PossoBuild shall not be liable for any indirect, incidental, or consequential damages arising from your use of the platform. Our total liability shall not exceed the fees paid by you in the three months preceding the claim.',
        ],
      },
      {
        heading: '6. Termination',
        paragraphs: [
          'Either party may terminate access to the platform with written notice as specified in your agreement.',
          'Upon termination, your data will be retained for 30 days and then deleted, unless a longer retention period is required by applicable law.',
        ],
      },
      {
        heading: '7. Governing Law',
        paragraphs: [
          'These Terms are governed by the laws of India and, where applicable, the laws of the United States.',
          'Disputes shall be resolved through binding arbitration in the jurisdiction relevant to the contracting entity.',
        ],
      },
      {
        heading: '8. Contact',
        paragraphs: ['For questions regarding these Terms, contact legal@possobuild.ai.'],
      },
    ],
  },
  privacy: {
    slug: 'privacy',
    title: 'Privacy Policy',
    summary: 'This policy explains what personal information PossoBuild collects, how it is used, and the rights available to users across supported jurisdictions.',
    meta: [
      { label: 'Effective Date', value: LEGAL_DATE },
      { label: 'Last Updated', value: LEGAL_DATE },
      { label: 'URL', value: 'possobuild.ai/privacy' },
    ],
    sections: [
      {
        heading: '1. Who We Are',
        paragraphs: [
          'PossoBuild operates the AI Workforce Intelligence Platform available at possobuild.ai. We are incorporated in both India and the United States.',
          'This Privacy Policy applies to all users of our website, platform, and products.',
        ],
      },
      {
        heading: '2. What Data We Collect',
        bullets: [
          'Account data: name, email address, organisation, and role.',
          'Interview and assessment data: responses, transcripts, and evaluation outputs.',
          'Usage data: pages visited, features used, session duration.',
          'Technical data: IP address, browser type, device information.',
          'Communication data: emails and messages sent to PossoBuild.',
        ],
      },
      {
        heading: '3. How We Use Your Data',
        bullets: [
          'To deliver and improve the PossoBuild platform and its AI models.',
          'To generate interview assessments and workforce intelligence reports.',
          'To communicate with you about your account, updates, and support.',
          'To comply with legal obligations under applicable law.',
          'To conduct internal research and product development using anonymised data only.',
        ],
      },
      {
        heading: '4. Your Rights',
        paragraphs: [
          'Depending on your location, you may have rights to access, correct, delete, port, or object to the processing of your personal data.',
          'PossoBuild does not sell personal data. To exercise your rights, email privacy@possobuild.ai. We will respond within 30 days.',
        ],
      },
      {
        heading: '5. Regulatory Compliance',
        paragraphs: [
          'PossoBuild is committed to compliance with the EU General Data Protection Regulation (GDPR), the California Consumer Privacy Act (CCPA), and the India Digital Personal Data Protection Act (DPDP Act, 2023).',
          'We act as a Data Processor for interview and assessment data on behalf of our enterprise customers, who are the Data Controllers.',
        ],
      },
      {
        heading: '6. Data Retention',
        paragraphs: [
          'We retain personal data only for as long as necessary to provide our services or as required by law.',
          'Interview transcripts and assessment data are retained for the period specified in your enterprise agreement, and deleted upon request or contract termination.',
        ],
      },
      {
        heading: '7. Data Sharing',
        paragraphs: [
          'We do not sell personal data.',
          'We share data only with your organisation as the Data Controller, trusted sub-processors who support platform operations under strict data processing agreements, and authorities where required by law.',
        ],
      },
      {
        heading: '8. Contact',
        paragraphs: ['Privacy queries: privacy@possobuild.ai.'],
      },
    ],
  },
  security: {
    slug: 'security',
    title: 'Security',
    summary: 'Security is built into PossoBuild from the ground up, with encrypted data handling, controlled access, and enterprise-grade infrastructure on Microsoft Azure.',
    meta: [
      { label: 'Last Updated', value: LEGAL_DATE },
      { label: 'URL', value: 'possobuild.ai/security' },
    ],
    sections: [
      {
        heading: 'Data Encryption',
        paragraphs: [
          'All data transmitted to and from PossoBuild is encrypted in transit using TLS 1.2 or higher.',
          'Data at rest is encrypted using AES-256. Interview transcripts and assessment outputs are stored in encrypted, access-controlled environments.',
        ],
      },
      {
        heading: 'Access Controls',
        paragraphs: [
          'Access to PossoBuild systems is governed by role-based access controls (RBAC).',
          'Internal access to customer data is restricted to authorised personnel only, on a need-to-know basis, and is logged and audited.',
        ],
      },
      {
        heading: 'Infrastructure',
        paragraphs: [
          'PossoBuild is hosted on Microsoft Azure, leveraging enterprise-grade cloud infrastructure with built-in redundancy, availability guarantees, and geographic data residency controls.',
        ],
      },
      {
        heading: 'AI Model Security',
        paragraphs: [
          'Customer interview data is never used to train shared AI models without explicit consent.',
          'Our AI pipeline operates under strict data isolation — one customer\'s data is never accessible to another.',
        ],
      },
      {
        heading: 'Responsible Disclosure',
        paragraphs: [
          'If you discover a security vulnerability in our platform, please report it responsibly to security@possobuild.ai.',
          'We commit to acknowledging your report within 48 hours and resolving confirmed vulnerabilities within 30 days.',
        ],
      },
      {
        heading: 'Compliance Roadmap',
        paragraphs: [
          'PossoBuild is working toward SOC 2 Type II certification.',
          'Enterprise customers may request our current security posture documentation and sub-processor list by contacting security@possobuild.ai.',
        ],
      },
    ],
  },
  cookies: {
    slug: 'cookies',
    title: 'Cookie Policy',
    summary: 'PossoBuild uses cookies and similar technologies to support core website functionality and to understand how visitors use the site.',
    meta: [
      { label: 'Effective Date', value: LEGAL_DATE },
      { label: 'URL', value: 'possobuild.ai/cookies' },
    ],
    sections: [
      {
        heading: 'What Are Cookies',
        paragraphs: [
          'Cookies are small text files stored on your device when you visit a website.',
          'They help us remember your preferences and improve your experience.',
        ],
      },
      {
        heading: 'Cookies We Use',
        bullets: [
          'Essential cookies: required for the platform to function. Examples include session authentication and security tokens.',
          'Analytics cookies: help us understand how visitors navigate our site so we can improve it. Data is aggregated and anonymised.',
          'Preference cookies: remember your settings and preferences across visits.',
        ],
      },
      {
        heading: 'Your Choices',
        paragraphs: [
          'You can control non-essential cookies through your browser settings or our cookie preference centre, available via the cookie banner on first visit.',
          'Disabling analytics cookies does not affect your ability to use the platform.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: ['Cookie queries: privacy@possobuild.ai.'],
      },
    ],
  },
  accessibility: {
    slug: 'accessibility',
    title: 'Accessibility',
    summary: 'PossoBuild is committed to making the platform usable across assistive technologies and interaction methods, with ongoing work toward WCAG 2.1 AA alignment.',
    meta: [
      { label: 'Last Updated', value: LEGAL_DATE },
      { label: 'URL', value: 'possobuild.ai/accessibility' },
    ],
    sections: [
      {
        heading: 'Our Commitment',
        paragraphs: [
          'We design and develop PossoBuild with reference to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA.',
          'We continuously review our platform to identify and address accessibility gaps.',
        ],
      },
      {
        heading: 'Current Status',
        paragraphs: [
          'PossoBuild is an actively developed platform. We are working to meet WCAG 2.1 AA standards across all core interview and assessment flows.',
          'Known gaps are logged and prioritised in our engineering roadmap.',
        ],
      },
      {
        heading: 'Supported Assistive Technologies',
        bullets: [
          'Screen readers including NVDA, JAWS, and VoiceOver.',
          'Keyboard-only navigation.',
          'Browser zoom up to 200% without loss of functionality.',
        ],
      },
      {
        heading: 'Feedback',
        paragraphs: [
          'If you experience any accessibility barriers on our platform, please contact us at accessibility@possobuild.ai.',
          'We aim to respond within 5 business days.',
        ],
      },
    ],
  },
  contact: {
    slug: 'contact',
    title: 'Contact Us',
    summary: 'We\'d love to hear from you. Whether you\'re exploring the platform, have a support question, or want to discuss an enterprise deployment, reach out and we\'ll respond within one business day.',
    meta: [{ label: 'URL', value: 'possobuild.ai/contact' }],
    sections: [
      {
        heading: 'General Enquiries',
        paragraphs: ['marketing@possobuild.ai'],
      },
      {
        heading: 'Sales and Pilots',
        paragraphs: ['sales@possobuild.ai'],
      },
      {
        heading: 'Privacy and Data Rights',
        paragraphs: ['privacy@possobuild.ai'],
      },
      {
        heading: 'Security',
        paragraphs: ['security@possobuild.ai'],
      },
    ],
  },
}